import { useState, useEffect } from 'react';
import { AlertCircle, User, CheckCircle, RefreshCw, FileText, Shield, Github } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customInstance } from '@edison/api-client-react';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import { useAuthStore } from '@/store/auth-store.js';

interface UserProfile {
  id: string;
  version: number;
  qualifications: string | null;
  relevantExpertise: string | null;
  employmentHistory: Array<{title: string; company: string; period: string; description: string}> | null;
  domains: string[] | null;
  competentProfessionalSummary: string | null;
  atoRelevanceNote: string | null;
  extractedAt: string | null;
  createdAt: string;
}

function useUserProfile() {
  return useQuery({
    queryKey: ['user-profile'],
    queryFn: () =>
      customInstance<{ data: UserProfile | null }>({ url: '/api/users/me/profile', method: 'GET' })
        .then((r: { data: UserProfile | null }) => r.data)
        .catch((err: unknown) => {
          if ((err as { response?: { status?: number } })?.response?.status === 404) return null;
          throw err;
        }),
    retry: false,
  });
}

function useReprocessProfile(onSuccess: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      customInstance<{ data: UserProfile }>({
        url: '/api/users/me/profile/reprocess',
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['user-profile'] });
      onSuccess();
    },
  });
}

function useSubmitProfile(onSuccess: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { rawText: string; mode: 'replace' | 'merge' }) =>
      customInstance<{ data: UserProfile }>({
        url: '/api/users/me/profile',
        method: 'POST',
        data: body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['user-profile'] });
      localStorage.setItem('edison:setup-profile', 'true');
      onSuccess();
    },
  });
}

interface MeUser {
  id: string;
  githubUsername: string | null;
  [key: string]: unknown;
}

function useMe() {
  return useQuery({
    queryKey: ['user-me'],
    queryFn: () =>
      customInstance<{ data: MeUser }>({ url: '/api/users/me', method: 'GET' })
        .then((r: { data: MeUser }) => r.data),
  });
}

function useUpdateGithubUsername(onSuccess: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (githubUsername: string) =>
      customInstance({
        url: '/api/users/me/github',
        method: 'PATCH',
        data: { githubUsername },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['user-me'] });
      onSuccess();
    },
  });
}

function GitHubUsernameField() {
  const { data: me, isLoading } = useMe();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const { mutate: save, isPending } = useUpdateGithubUsername(() => setEditing(false));

  useEffect(() => {
    if (me?.githubUsername) setValue(me.githubUsername);
  }, [me?.githubUsername]);

  if (isLoading) {
    return <div className="h-14 rounded-lg skeleton" />;
  }

  return (
    <div className="rounded-lg border p-4">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <Github className="h-3.5 w-3.5" aria-hidden="true" />
          GitHub Username
        </div>
        {!editing && (
          <Button variant="ghost" size="sm" className="text-xs h-6 px-2" onClick={() => { setValue(me?.githubUsername ?? ''); setEditing(true); }}>
            {me?.githubUsername ? 'Edit' : 'Set'}
          </Button>
        )}
      </div>
      {editing ? (
        <div className="flex items-center gap-2 mt-2">
          <label htmlFor="github-username" className="sr-only">GitHub Username</label>
          <input
            id="github-username"
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="e.g. octocat"
            className="flex-1 rounded-lg border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            autoFocus
          />
          <Button
            size="sm"
            onClick={() => save(value.trim())}
            disabled={isPending || !value.trim()}
          >
            {isPending ? 'Saving...' : 'Save'}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
        </div>
      ) : (
        <p className="text-sm mt-1">
          {me?.githubUsername ? (
            <span className="font-medium">{me.githubUsername}</span>
          ) : (
            <span className="text-muted-foreground italic">Not set — link your GitHub username for commit attribution.</span>
          )}
        </p>
      )}
      <p className="text-xs text-muted-foreground mt-2">
        Used to match commits to your account when your Git email differs from your Edison email.
      </p>
    </div>
  );
}

function isStale(createdAt: string): boolean {
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
  return new Date(createdAt) < oneYearAgo;
}

const ROLE_LABELS: Record<string, string> = {
  tenant_admin: 'Tenant Admin',
  r_and_d_manager: 'R&D Manager',
  member: 'Member',
  viewer: 'Viewer',
};

export function ProfilePage() {
  const [rawText, setRawText] = useState('');
  const [showForm, setShowForm] = useState(false);
  const { user: authUser } = useAuthStore();

  const { data: profile, isLoading, error, refetch } = useUserProfile();
  const { mutate: submitProfile, isPending, error: submitError } = useSubmitProfile(() => {
    setRawText('');
    setShowForm(false);
  });
  const { mutate: reprocessProfile, isPending: isReprocessing, error: reprocessError } = useReprocessProfile(() => {});

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 rounded-lg skeleton" />
        <div className="h-32 w-full rounded-lg skeleton" />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-destructive text-sm">
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>Failed to load profile.</span>
          </div>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const canSubmit = rawText.trim().length >= 50;

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">My Profile</h1>
          {authUser?.role && (
            <Badge variant="default" aria-label={`Your role: ${ROLE_LABELS[authUser.role] ?? authUser.role}`}>
              <Shield className="h-3 w-3 mr-1" aria-hidden="true" />
              {ROLE_LABELS[authUser.role] ?? authUser.role}
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Your competency profile and credentials for R&D claims.
        </p>
      </div>

      <GitHubUsernameField />

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">Competency Profile</h1>
          {profile && (
            <Badge variant="default">v{profile.version}</Badge>
          )}
        </div>
        {profile && !showForm && (
          <Button variant="secondary" size="sm" onClick={() => setShowForm(true)}>
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            Update Profile
          </Button>
        )}
      </div>

      {/* Staleness warning */}
      {profile && isStale(profile.createdAt) && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-2 text-amber-800">
          <AlertCircle className="h-4 w-4 mt-1 shrink-0" aria-hidden="true" />
          <p className="text-sm">
            Your competency profile was last updated over a year ago. Update it to keep your TFA panel records current.
          </p>
        </div>
      )}

      {/* AusIndustry relevance warning */}
      {profile?.atoRelevanceNote && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-2 text-amber-800">
          <AlertCircle className="h-4 w-4 mt-1 shrink-0" aria-hidden="true" />
          <div className="text-sm">
            <p className="font-medium mb-1">AusIndustry Relevance Note</p>
            <p>{profile.atoRelevanceNote}</p>
          </div>
        </div>
      )}

      {/* Existing profile */}
      {profile && (() => {
        const hasContent = !!(profile.competentProfessionalSummary || profile.qualifications || profile.relevantExpertise || (Array.isArray(profile.domains) && profile.domains.length > 0) || (Array.isArray(profile.employmentHistory) && profile.employmentHistory.length > 0));

        return (
          <div className="rounded-lg border divide-y">
            {!hasContent && (
              <div className="p-4 space-y-3">
                <div className="flex items-start gap-2 text-amber-700">
                  <AlertCircle className="h-4 w-4 mt-1 shrink-0" aria-hidden="true" />
                  <div className="text-sm">
                    <p className="font-medium">Profile extraction returned no data</p>
                    <p className="text-muted-foreground mt-1">
                      The raw AI response was saved. Click Reprocess to re-parse it with the fixed schema — no new API call needed.
                    </p>
                  </div>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => reprocessProfile()}
                  disabled={isReprocessing}
                >
                  <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                  {isReprocessing ? 'Reprocessing…' : 'Reprocess stored response'}
                </Button>
                {reprocessError && (
                  <p className="text-xs text-destructive mt-1">Reprocessing failed. Please try again.</p>
                )}
              </div>
            )}

            {/* AusIndustry Competent Professional Summary — shown first, most prominent */}
            <div className="p-4 space-y-2">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Shield className="h-3.5 w-3.5" aria-hidden="true" />
                Competent Professional Summary
              </div>
              {profile.competentProfessionalSummary ? (
                <>
                  <p className="text-sm leading-relaxed">{profile.competentProfessionalSummary}</p>
                  <p className="text-xs text-muted-foreground">
                    This summary supports your role as a competent professional under Division 355-25.
                  </p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground italic">Summary not available for this version — reprocess your profile to generate it.</p>
              )}
            </div>

            {profile.qualifications && (
              <div className="p-4 space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Qualifications</p>
                <p className="text-sm">{profile.qualifications}</p>
              </div>
            )}

            {profile.relevantExpertise && (
              <div className="p-4 space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Relevant Expertise</p>
                <p className="text-sm">{profile.relevantExpertise}</p>
              </div>
            )}

            {Array.isArray(profile.domains) && profile.domains.length > 0 && (
              <div className="p-4 space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Technical Domains</p>
                <div className="flex flex-wrap gap-1">
                  {profile.domains.map((d: string) => (
                    <span
                      key={d}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-background"
                    >
                      {d}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {Array.isArray(profile.employmentHistory) && profile.employmentHistory.length > 0 && (
              <div className="p-4 space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Employment History</p>
                <div className="space-y-2">
                  {profile.employmentHistory.map((job: {title: string; company: string; period: string; description: string}, i: number) => (
                    <div key={i} className="text-sm">
                      <span className="font-medium">{job.title}</span>
                      {' at '}
                      <span>{job.company}</span>
                      <span className="text-muted-foreground"> ({job.period})</span>
                      {job.description && <p className="text-muted-foreground text-xs mt-1">{job.description}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="p-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <CheckCircle className="h-3 w-3 text-green-600" aria-hidden="true" />
                Version {profile.version} · Extracted{' '}
                {profile.extractedAt
                  ? new Date(profile.extractedAt).toLocaleDateString('en-AU')
                  : 'manually'}
              </p>
            </div>
          </div>
        );
      })()}

      {/* Empty state */}
      {!profile && !showForm && (
        <div className="text-center py-12 rounded-lg border border-dashed">
          <User className="h-10 w-10 text-muted-foreground mx-auto mb-3" aria-hidden="true" />
          <p className="font-medium">No competency profile yet</p>
          <p className="text-sm text-muted-foreground mt-1 mb-4">
            Paste your LinkedIn or CV text to create your AusIndustry-ready competency profile.
          </p>
          <Button variant="primary" onClick={() => setShowForm(true)}>
            <FileText className="h-3.5 w-3.5" aria-hidden="true" />
            Create Profile
          </Button>
        </div>
      )}

      {/* Update form */}
      {showForm && (
        <div className="rounded-lg border p-4 space-y-4">
          <div>
            <label htmlFor="rawText" className="block text-sm font-medium mb-1">
              {profile ? 'Paste updated LinkedIn or CV text' : 'Paste your LinkedIn or CV text'}
            </label>
            <p className="text-xs text-muted-foreground mb-2">
              Edison will extract your qualifications, expertise, employment history, and generate
              an AusIndustry Division 355-25 competent professional summary.
            </p>
            <textarea
              id="rawText"
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              rows={10}
              placeholder="Paste your LinkedIn profile or CV text here..."
              data-tour="profile-paste"
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm font-mono placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-y"
            />
            {rawText.trim().length > 0 && rawText.trim().length < 50 && (
              <p className="text-xs text-destructive mt-1">Please paste more content (minimum 50 characters).</p>
            )}
          </div>

          {submitError && (
            <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>Extraction failed. Please try again.</span>
            </div>
          )}

          {profile ? (
            // Existing profile: show Replace vs Merge choice
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                <strong>Replace</strong> — create a new version using only the freshly extracted fields.{' '}
                <strong>Merge</strong> — carry forward any fields the new extraction leaves blank from your current version.
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="primary"
                  onClick={() => submitProfile({ rawText, mode: 'replace' })}
                  disabled={isPending || !canSubmit}
                >
                  {isPending ? 'Extracting…' : 'Replace'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => submitProfile({ rawText, mode: 'merge' })}
                  disabled={isPending || !canSubmit}
                >
                  {isPending ? 'Extracting…' : 'Merge'}
                </Button>
                <Button variant="ghost" onClick={() => { setShowForm(false); setRawText(''); }}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            // No existing profile: just create
            <div className="flex items-center gap-2">
              <Button
                variant="primary"
                onClick={() => submitProfile({ rawText, mode: 'replace' })}
                disabled={isPending || !canSubmit}
              >
                {isPending ? 'Extracting…' : 'Create Profile'}
              </Button>
              <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
